import { ApiError } from '@discovr/common';
import { errorMessage } from './errorCopy';
import { dateRuleMessage, ROW_CHANGED_CODES, shopifyEditCopy } from './shopifyEdit';

const nonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/**
 * Copy for recreating a code Shopify renamed, under its old name, from Discovr.
 * The code never changes; the server reports whether it placed the code with
 * the sent terms ("recreated") or found it already in Shopify and kept
 * Shopify's terms ("relinked").
 */
export const shopifyRecreateCopy = {
  action: 'Recreate in Discovr',
  liveAlternative: (code: string) =>
    `Or recreate ${code} in Shopify Admin (Discounts), then select the refresh button at the top of Coupons.`,
  title: 'Recreate code',
  description: (code: string) =>
    `Puts ${code} back in Shopify with these settings. The code and link stay the same. If ${code} already exists in Shopify, Discovr links to it and keeps Shopify’s settings instead.`,
  codeLabel: 'Code',
  codeLocked: (code: string) => `Posted content uses ${code}, so the code can’t change.`,
  valueLocked: (code: string) =>
    `${code} includes its discount value, so the discount type and value can’t change.`,
  submit: (code: string) => `Recreate ${code}`,
  submitting: 'Recreating…',
  // Toasts stay generic: they render outside the masked containers.
  recreated: 'The code was recreated in Shopify and works at checkout again.',
  relinked:
    'The code already existed in Shopify, so Discovr linked to it. Shopify’s discount settings were kept, not the ones you entered.',
  errors: {
    generic: 'Couldn’t recreate the code',
    notRenamed:
      'This code is no longer marked as changed in Shopify, so there is nothing to recreate. The list has been refreshed.',
    onOtherDiscount: (code?: string) =>
      code
        ? `${code} already exists in Shopify on a discount other codes use, so it can’t be recreated for this discount code. Remove ${code} from that discount in Shopify, then try again.`
        : 'The code already exists in Shopify on a discount other codes use, so it can’t be recreated for this discount code. Remove it from that discount in Shopify, then try again.',
    // The form already locks the value of a code that spells it out, so a
    // refusal usually comes from another code on the same discount.
    valueInCode:
      'A code on this discount includes its discount value, so the value can’t change. Keep the original value and try again.',
    gone: 'This code no longer exists, so there is nothing to recreate. The list has been refreshed.',
    editInProgress:
      'This code is being changed right now, so it wasn’t recreated. The list has been refreshed; check it, then try again if needed.',
    store: {
      SHOPIFY_NOT_CONNECTED:
        'This campaign’s Shopify store isn’t connected, so the code can’t be recreated.',
      SHOPIFY_STORE_MISMATCH:
        'This campaign was set up with a different Shopify store, so the code can’t be recreated.',
      SHOPIFY_UNINSTALL_IN_PROGRESS:
        'Discovr is being removed from your Shopify store, so the code can’t be recreated.',
      SHOPIFY_DATA_REMOVED:
        'This campaign’s Shopify data was removed after the store was uninstalled, so the code can’t be recreated.',
    } as Readonly<Record<string, string>>,
  },
} as const;

export interface RecreateFailure {
  message: string;
  closeDialog: boolean;
  refreshList: boolean;
  /** A code on the discount spells out its value, so the value fields lock. */
  lockValue?: boolean;
}

// The coupon loader's 404; any other 404 (e.g. a route) is not "the code is gone".
const COUPON_NOT_FOUND_MESSAGE = 'coupon not found';

/** How a refused recreate is shown, from the error code and `details` only. */
export function recreateFailure(err: unknown, code: string): RecreateFailure {
  const copy = shopifyRecreateCopy.errors;
  const stay = (message: string, refreshList = false) => ({
    message,
    closeDialog: false,
    refreshList,
  });
  const close = (message: string) => ({ message, closeDialog: true, refreshList: true });
  if (err instanceof ApiError) {
    if (
      err.httpStatus === 404 &&
      err.code === 'NOT_FOUND' &&
      err.message === COUPON_NOT_FOUND_MESSAGE
    ) {
      return close(copy.gone);
    }
    const store = Object.hasOwn(copy.store, err.code) ? copy.store[err.code] : undefined;
    if (err.httpStatus === 409 && store) return close(store);
    switch (err.code) {
      case 'SHOPIFY_CODE_NOT_RENAMED':
        return close(copy.notRenamed);
      case 'SHOPIFY_CODE_ON_OTHER_DISCOUNT': {
        const details = isRecord(err.details) ? err.details : {};
        return stay(copy.onOtherDiscount(nonEmptyString(details.code) ? details.code : undefined));
      }
      case 'SHOPIFY_CODE_TAKEN':
        if (err.httpStatus === 409) return stay(shopifyEditCopy.errors.codeTaken);
        break;
      case 'SHOPIFY_VALUE_IN_CODE':
        return { ...stay(copy.valueInCode, true), lockValue: true };
      case 'SHOPIFY_EDIT_IN_PROGRESS':
        // The row may already have moved, so the open form is stale.
        return close(copy.editInProgress);
    }
    if (ROW_CHANGED_CODES.has(err.code)) return close(errorMessage(err, copy.generic));
  }
  if (err instanceof ApiError && err.httpStatus === 404) return stay(copy.generic);
  return stay(dateRuleMessage(err) ?? errorMessage(err, copy.generic));
}
